import { useTailgateGrid } from '../hooks/useTailgateGrid'
import { useUpdateTailgateGrid } from '../hooks/useUpdateTailgateGrid'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import { Flex } from '@chakra-ui/react'

import { ColorScheme, Button as DarkButton } from '@hivewatch/dark'

import { Button } from '@/components/ui'
import { BUTTON_PRESS } from '@/utils/analytics'

import { getGridProperties } from '../utils/utils'
import { GridCameraFrame } from './GridCameraFrame'
import { GridCoordsBox } from './GridCoordsBox'

interface GridModalBodyIProps {
  colorScheme?: ColorScheme
  hideActions?: boolean
  onClose: () => void
  onOpenTestModal?: () => void
}

export const GridModalBody = ({
  colorScheme,
  hideActions,
  onClose,
  onOpenTestModal,
}: GridModalBodyIProps) => {
  const { t } = useTranslation()
  const { shouldEnableConfigureTailgateGrid, isImplementer } = usePermissions()
  const {
    outBox,
    inBox,
    isInitialGridLoading,
    isImageLoading,
    imageUrl,
    isDirty,
    associateDeviceValues,
    onSaveCallback,
    trackTailgateEvent,
    videoDimensions,
  } = useTailgateGrid()

  const { isUpdateLoading, updateGrid, deleteGrid } = useUpdateTailgateGrid({
    associatedDevices: associateDeviceValues,
    onClose,
    onSaveCallback,
  })

  const isLoading = isImageLoading || isInitialGridLoading

  // If existing grid is edited boxes should be geometrically valid or empty (this unset grid when saved)
  // If grid is set for the first time boxes should be gemetrically valid but cannot be empty
  const { isGridValid, isGridEmpty } = getGridProperties(inBox, outBox)

  const onSave = () => {
    trackTailgateEvent(`${BUTTON_PRESS} Tailgate Editor - Save Changes`)

    isGridEmpty ? deleteGrid() : updateGrid(inBox, outBox, videoDimensions)
  }

  const onCancel = () => {
    trackTailgateEvent(
      `${BUTTON_PRESS} Tailgate Editor - Tailgate Editor - Cancel`
    )
    onClose()
  }

  // Submit button should be disabled when:
  const isSubmitDisabled =
    !shouldEnableConfigureTailgateGrid || // User has role without edit permission
    isLoading || // Frame or initial grid are loading
    !imageUrl || // Frame cannot be loaded
    !isGridValid || // Grid is not setup correctly
    !isDirty // Grid has not been edited

  return (
    <Flex direction='column'>
      <Flex direction='row' pb={3}>
        <GridCameraFrame imageUrl={imageUrl} isImageLoading={isImageLoading} />
        <Flex direction='column' flex={1}>
          <GridCoordsBox
            backgroundColour='#D3F8EF'
            box={inBox}
            colorScheme={colorScheme}
            iconColour='#006C53'
            isValid={inBox?.isValid}
            loading={isInitialGridLoading}
            subTitle={t('tailgate:modal_body.unsecured_subtitle')}
            title={t('tailgate:modal_body.unsecured_title')}
            tone='success'
          />
          <GridCoordsBox
            backgroundColour='#FFDAD6'
            box={outBox}
            colorScheme={colorScheme}
            iconColour='#BA2D1A'
            isValid={outBox?.isValid}
            loading={isInitialGridLoading}
            subTitle={t('tailgate:modal_body.secured_subtitle')}
            title={t('tailgate:modal_body.secured_title')}
            tone='error'
          />
        </Flex>
      </Flex>
      {!hideActions && (
        <Flex
          alignItems='center'
          direction='row'
          justifyContent='space-between'
          mt={2}
          p={3}
        >
          <Flex alignItems='center' gap={2}>
            {isImplementer &&
              (colorScheme ? (
                <DarkButton
                  colorScheme={colorScheme}
                  onClick={onOpenTestModal}
                  variant='secondary'
                >
                  {t('common:enter_grid_testing_zone')}
                </DarkButton>
              ) : (
                <Button onClick={onOpenTestModal} variant='secondary'>
                  {t('common:enter_grid_testing_zone')}
                </Button>
              ))}
          </Flex>
          <Flex direction='row' gap={3}>
            {colorScheme ? (
              <>
                <DarkButton
                  colorScheme={colorScheme}
                  onClick={onCancel}
                  variant='tertiary'
                >
                  {t('common:cancel')}
                </DarkButton>
                <DarkButton
                  colorScheme={colorScheme}
                  disabled={isSubmitDisabled}
                  isLoading={isUpdateLoading}
                  leftIcon={<HiOutlineCheckCircle size='24px' />}
                  onClick={onSave}
                  variant='primary'
                >
                  {isUpdateLoading
                    ? t('common:saving')
                    : t('common:save_changes')}
                </DarkButton>
              </>
            ) : (
              <>
                <Button onClick={onCancel} variant='secondary'>
                  {t('common:cancel')}
                </Button>
                <Button
                  isDisabled={isSubmitDisabled}
                  isLoading={isUpdateLoading}
                  leftIcon={<HiOutlineCheckCircle size='24px' />}
                  loadingText={t('common:saving')}
                  onClick={onSave}
                >
                  {t('common:save_changes')}
                </Button>
              </>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
